import { useEffect, useRef, useState } from 'react';

import { logout, selectNeedsAuthOrConsent, selectStatus, useAuthStore } from '@features/auth';

/**
 * The LOG IN of the payment link. Somebody with no session gets the login; anybody holding another session (another
 * account or a guest one) is logged out first, so the renter can log in. Nothing is touched until they ask.
 */
export function useSpanPaymentLinkLogin(onLoggedIn: () => void) {
  const hasSession = useAuthStore(selectStatus) !== 'unauthenticated';
  const isLoggedIn = useAuthStore((state) => !selectNeedsAuthOrConsent(state));
  const [loginRequested, setLoginRequested] = useState<boolean>(false);
  const wasLoggedInRef = useRef<boolean>(isLoggedIn);

  useEffect(() => {
    const hasJustLoggedIn = isLoggedIn && !wasLoggedInRef.current;
    wasLoggedInRef.current = isLoggedIn;

    if (!hasJustLoggedIn || !loginRequested) return;
    setLoginRequested(false);
    onLoggedIn();
    // Reacts to the login only; the callback is recreated each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, loginRequested]);

  const requestLogin = (): void => {
    if (hasSession) logout();
    setLoginRequested(true);
  };

  const handleLoginOpenChange = (open: boolean): void => {
    if (!open) setLoginRequested(false);
  };

  return {
    /** Somebody already holds a session (another account or a guest one): the login logs them out first. */
    hasSession,
    showLogin: loginRequested && !isLoggedIn,
    requestLogin,
    handleLoginOpenChange,
  };
}
